import React, { useEffect, useState } from "react";
import classes from "./ChangeStudioModal.module.css";
import { SmallModal } from "../../components/UI/SmallModal/SmallModal";
import { getProjectCoverImage } from "./projectThumbnailUtils";

export const StudioArbeitenModal = ({
  creative,
  open,
  onClose,
  selectedProjectIds = [],
  onSaveSelection,
}) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 742 : false
  );
  const [localSelectedIds, setLocalSelectedIds] = useState(
    selectedProjectIds || []
  );

  const projects = creative?.projects?.items || [];

  // Sync local selection when parent selection or modal open state changes
  useEffect(() => {
    setLocalSelectedIds(selectedProjectIds || []);
  }, [selectedProjectIds, open]);

  // Handle window resize to detect mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 742);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Lock body scroll when full-page modal is open on mobile
  useEffect(() => {
    if (isMobile && open) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isMobile, open]);

  const toggleProject = (projectId) => {
    setLocalSelectedIds((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  };

  const handleSave = () => {
    if (onSaveSelection) {
      onSaveSelection(localSelectedIds);
    }
    onClose();
  };

  const isChecked = (projectId) => localSelectedIds.includes(projectId);

  const renderDuration = (project) => {
    const audioData = project?.audios?.[0];
    const durationMs = audioData?.durationMs;
    if (!durationMs) return "";

    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const content = (
    <div className={classes.chooseStudio}>
      <header className={classes.phoneHeader}>
        <div className={classes.phoneHeaderBackButton} onClick={onClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="19"
            viewBox="0 0 11 19"
            fill="none"
          >
            <line
              x1="9.60104"
              y1="0.601041"
              x2="0.602493"
              y2="9.59959"
              stroke="#1F1F1F"
              strokeWidth="1.7"
            />
            <line
              x1="0.624584"
              y1="8.42346"
              x2="9.62313"
              y2="18.1719"
              stroke="#1F1F1F"
              strokeWidth="1.7"
            />
          </svg>
          <span className="mediumLarge">Zurück</span>
        </div>
      </header>

      <header className={classes.header}>
        <h2 className={`${classes.title} titleSemibold2`}>
          Beispiele aus deinen Arbeiten hinzufügen.
        </h2>
        <div className={classes.phoneHeaderTitleContainer}>
          <h2 className={`${classes.phoneTitle} titleSemibold`}>
            Beispiele aus deinen Arbeiten hinzufügen.
          </h2>
        </div>
        <svg
          className={classes.closeButton}
          onClick={onClose}
          xmlns="http://www.w3.org/2000/svg"
          width="17"
          height="17"
          viewBox="0 0 17 17"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z"
            fill="#1F1F1F"
          />
        </svg>
      </header>

      <div className={classes.studiosList}>
        {projects.map((project) => {
          const coverImage = getProjectCoverImage(project);
          const duration = renderDuration(project);
          const album = project?.audios?.[0]?.album || "";

          return (
            <div
              key={project.id}
              className={classes.option}
              onClick={() => toggleProject(project.id)}
            >
              <div className={classes.studioInfo}>
                <div className={classes.studioImage}>
                  {coverImage && <img src={coverImage} alt={project.title} />}
                </div>
                <div className={classes.containerLabels}>
                  <label
                    style={{ marginBottom: "6px" }}
                    className={`${classes.titleTruncate} titleSemibold3`}
                  >
                    {project.title}
                  </label>
                  <label
                    className={`${classes.label} slimRegularSmallText`}
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    {album && <span>{album}</span>}
                    {duration && <span>{duration}</span>}
                  </label>
                </div>
              </div>
              <input
                className={classes.inputs}
                type="checkbox"
                id={project.id}
                value={project.id}
                checked={isChecked(project.id)}
                onChange={() => toggleProject(project.id)}
              />
            </div>
          );
        })}
      </div>

      <button
        style={{
          backgroundColor: localSelectedIds.length
            ? "var(--dark-grey)"
            : "var(--blue-grey-ultralight)",
          color: localSelectedIds.length ? "var(--white)" : "var(--grey-1)",
        }}
        className={`${classes.saveButton} mediumLarge`}
        onClick={handleSave}
      >
        Hinzufügen
      </button>

      <footer className={classes.phoneFooter}>
        <button
          style={{
            backgroundColor: localSelectedIds.length
              ? "var(--dark-grey)"
              : "var(--blue-grey-ultralight)",
            color: localSelectedIds.length ? "var(--white)" : "var(--grey-1)",
          }}
          className={`${classes.phoneSaveButton} mediumLarge`}
          onClick={handleSave}
        >
          Hinzufügen
        </button>
      </footer>
    </div>
  );

  if (isMobile && open) {
    return <div className={classes.fullPageContainer}>{content}</div>;
  }

  return (
    <SmallModal changeStudioModal={true} open={open} onBackdropClick={onClose}>
      {content}
    </SmallModal>
  );
};

export default StudioArbeitenModal;

